import { formatCurrency as formatCurrencyShared, formatInvoiceNumber as formatInvoiceNumberShared } from '../../../utils/formatUtils';
import { formatDateISO } from '../../../utils/dateUtils';

/**
 * Obtiene la fecha actual en formato ISO YYYY-MM-DD
 */
export const getCurrentDate = () => formatDateISO();

export const formatCurrency = (amount) => formatCurrencyShared(amount);

/**
 * Formatea el número de factura
 * @param {string} typeLetter - Letra del tipo de factura
 * @param {number} salePointNumber - Número del punto de venta
 * @param {number} receiptNumber - Número de recibo
 * @returns {string} - Número de factura formateado
 */
export const formatInvoiceNumber = (typeLetter, salePointNumber, receiptNumber) =>
  formatInvoiceNumberShared(typeLetter, salePointNumber, receiptNumber);

/**
 * Genera la etiqueta para mostrar en el autocomplete de contratos
 * @param {Object} option - Opción del contrato
 * @returns {string} - Etiqueta formateada
 */
export const getContractLabel = (option) => {
  if (typeof option === 'string') return option;

  if (option.fee_type_name !== 'Cuota') {
    return `Contrato: ${option.contract_number} - ${
      option.fee_type_name !== 'Cuota Incompleta' && option.fee_type_name !== 'Cuota Completa'
        ? `Cuota: ${option.fee_type_name}`
        : option.fee_number2
        ? `Cuotas: ${option.fee_number1} y ${option.fee_number2}`
        : `Cuota: ${option.fee_number1}`
    } - Fecha: ${option.month}/${option.year}`;
  } else {
    const feeNumbers = [option.fee_number1];
    if (option.fee_number2 !== null) {
      feeNumbers.push(option.fee_number2);
    }
    const feeNumbersString = feeNumbers.join(' - ');
    return `${option.contract_number} - Cuota: ${feeNumbersString} - ${option.month}/${option.year}`;
  }
};

/**
 * Ordena los contratos por contract_number primero, después por fecha (más reciente a menos reciente)
 * @param {Array} contracts - Array de contratos
 * @returns {Array} - Contratos ordenados
 */
export const sortContractsByDate = (contracts) => {
  return [...contracts].sort((a, b) => {
    if (a.contract_number !== b.contract_number) {
      return a.contract_number - b.contract_number;
    }
    const dateA = a.first_due_date ? new Date(a.first_due_date) : new Date(0);
    const dateB = b.first_due_date ? new Date(b.first_due_date) : new Date(0);
    return dateB - dateA;
  });
};

/**
 * Determina el stage de vencimiento basado en la fecha seleccionada
 * @param {string} selectedDate - Fecha seleccionada
 * @param {Object} contract - Contrato
 * @returns {Object} - Objeto con dueStage, amount y date
 */
export const determineDueStage = (selectedDate, contract) => {
  const currentDate = new Date(selectedDate);
  const firstDue = new Date(contract.first_due_date);
  const secondDue = new Date(contract.second_due_date);
  const thirdDue = new Date(contract.third_due_date);

  if (currentDate <= firstDue) {
    return {
      dueStage: 'first_due',
      amount: contract.first_due_amount,
      date: contract.first_due_date,
    };
  } else if (currentDate <= secondDue) {
    return {
      dueStage: 'second_due',
      amount: contract.second_due_amount,
      date: contract.second_due_date,
    };
  } else if (currentDate <= thirdDue) {
    return {
      dueStage: 'third_due',
      amount: contract.third_due_amount,
      date: contract.third_due_date,
    };
  } else {
    return {
      dueStage: 'generate_new_due',
      amount: 0,
      date: '',
    };
  }
};

/**
 * Formatea la información de cuotas para mostrar en la tabla
 * @param {Object} data - Datos del cupón
 * @returns {string} - Cuotas formateadas
 */
export const formatFeeNumbers = (data) => {
  // Determinar el tipo de cuota de forma más legible (igual lógica que transformCouponsData)
  if (data.is_adjudication) {
    return 'Adjudicación';
  } else if (data.is_score) {
    return 'Puntaje';
  } else if (data.is_advance_payment) {
    return 'Adelanto';
  } else if (data.fee_number1 === 0) {
    return 'Adecuación';
  } else {
    // Cuotas normales
    const cuotaNumero = data.fee_number1;
    const cuotaHasta = data.fee_number2;
    
    if (cuotaHasta && cuotaHasta !== 0 && cuotaHasta !== null && cuotaHasta !== undefined) {
      return `${cuotaNumero} - ${cuotaHasta}`;
    } else {
      return cuotaNumero.toString();
    }
  }
};

/**
 * Obtiene el importe según el stage de vencimiento
 * @param {Object} contract - Contrato
 * @param {string} dueStage - Stage de vencimiento
 * @returns {number} - Importe
 */
export const getAmountByDueStage = (contract, dueStage) => {
  switch (dueStage) {
    case 'first_due':
      return parseFloat(contract.first_due_amount);
    case 'second_due':
      return parseFloat(contract.second_due_amount);
    case 'third_due':
      return parseFloat(contract.third_due_amount);
    default:
      return 0;
  }
};

/**
 * Obtiene la fecha según el stage de vencimiento
 * @param {Object} contract - Contrato
 * @param {string} dueStage - Stage de vencimiento
 * @returns {string} - Fecha
 */
export const getDateByDueStage = (contract, dueStage) => {
  switch (dueStage) {
    case 'first_due':
      return contract.first_due_date?.slice(0, 10) || '';
    case 'second_due':
      return contract.second_due_date?.slice(0, 10) || '';
    case 'third_due':
      return contract.third_due_date?.slice(0, 10) || '';
    default:
      return 'Generar nuevo vencimiento';
  }
};

/**
 * Valida si un contrato ya existe en la tabla
 * @param {Array} tableData - Datos de la tabla
 * @param {string} contractUuid - UUID del contrato
 * @returns {boolean} - True si existe
 */
export const isContractDuplicate = (tableData, contractUuid) => {
  return tableData.some((item) => item.uuid === contractUuid);
};

/**
 * Calcula el total de cupones y monto
 * @param {Array} tableData - Datos de la tabla
 * @returns {Object} - Objeto con totalCoupons y totalAmount
 */
export const calculateTotals = (tableData) => {
  const totalCoupons = tableData.length;
  const totalAmount = tableData.reduce((acc, data) => acc + parseFloat(data.amount || 0), 0);

  return {
    totalCoupons,
    totalAmount,
  };
};

/**
 * Procesa cupones de NaranjaX para consolidación
 * @param {Array} coupons - Cupones de NaranjaX
 * @param {string} consolidateDate - Fecha de consolidación
 * @returns {Array} - Cupones procesados
 */
export const processNaranjaXCoupons = (coupons, consolidateDate) => {
  return coupons.map((coupon) => ({
    ...coupon,
    date: consolidateDate + ' 00:00:00',
    dueStage: 'consolidated',
  }));
};

/**
 * Cambia la fecha por días
 * @param {string} currentDate - Fecha actual
 * @param {number} days - Días a sumar/restar
 * @returns {string} - Nueva fecha
 */
export const changeDateByDays = (currentDate, days) => {
  const date = new Date(currentDate);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

/**
 * Valida los datos antes de guardar la cobranza
 * @param {Array} tableData - Datos de la tabla
 * @param {string} entidadSeleccionada - Entidad seleccionada
 * @param {Object} selectedSalePoint - Punto de venta seleccionado
 * @returns {Object} - Resultado de validación
 */
export const validateSaveData = (tableData, entidadSeleccionada, selectedSalePoint) => {
  const errors = [];

  if (!tableData || tableData.length === 0) {
    errors.push('No hay cupones para registrar');
  }

  if (!entidadSeleccionada) {
    errors.push('Debe seleccionar una entidad recaudadora');
  }

  if (!selectedSalePoint) {
    errors.push('Debe seleccionar un punto de venta');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Construye el payload para registrar el pago
 * @param {Array} tableData - Datos de la tabla
 * @param {string} entidadSeleccionada - Entidad seleccionada
 * @param {boolean} makeElectronicInvoice - Generar factura electrónica
 * @param {string} uuid - UUID del usuario
 * @param {Array} branch - Sucursal
 * @param {Object} selectedSalePoint - Punto de venta
 * @param {string} selectedDate - Fecha seleccionada
 * @returns {Object} - Payload para la API
 */
export const buildPaymentPayload = (
  tableData,
  entidadSeleccionada,
  makeElectronicInvoice,
  uuid,
  branch,
  selectedSalePoint,
  selectedDate
) => {
  return {
    paymentsToRegister: tableData.map((item) => ({
      uuid: item.uuid,
      first_due_amount: item.amount,
      cancellation_date: item.date,
      uuid_legal_entity: entidadSeleccionada,
    })),
    invoiceData: {
      make_electronic_invoice: makeElectronicInvoice,
      token:
        'PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9InllcyI/Pgo8c3NvIHZlcnNpb249IjIuMCI+CiAgICA8aWQgc3JjPSJDTj13c2FhaG9tbywgTz1BRklQLCBDPUFSLCBTRVJJQUxOVU1CRVI9Q1VJVCAzMzY5MzQ1MDIzOSIgZHN0PSJDTj13c2ZlLCBPPUFGSVAsIEM9QVIiIHVuaXF1ZV9pZD0iMTQxNzAyMDg0NiIgZ2VuX3RpbWU9IjE3MDAyMzg4NjYiIGV4cF90aW1lPSIxNzAwMjgyMTI2Ii8+CiAgICA8b3BlcmF0aW9uIHR5cGU9ImxvZ2luIiB2YWx1ZT0iZ3JhbnRlZCI+CiAgICAgICAgPGxvZ2luIGVudGl0eT0iMzM2OTM0NTAyMzkiIHNlcnZpY2U9IndzZmUiIHVpZD0iU0VSSUFMTlVNQkVSPUNVSVQgMjAzNzYyMjgyMjQsIENOPWNvbG9uaWFsIiBhdXRobWV0aG9kPSJjbXMiIHJlZ21ldGhvZD0iMjIiPgogICAgICAgICAgICA8cmVsYXRpb25zPgogICAgICAgICAgICAgICAgPHJlbGF0aW9uIGtleT0iMjAzNzYyMjgyMjQiIHJlbHR5cGU9IjQiLz4KICAgICAgICAgICAgPC9yZWxhdGlvbnM+CiAgICAgICAgPC9sb2dpbj4KICAgIDwvb3BlcmF0aW9uPgo8L3Nzbz4K',
      sign: 'orAFUYOf8eSKu7RGVFhpSzmaM2Lv8pqJAzMTuNugfba6fBqx3nGP1wzXs8YFMqt/l3H1pYVFss9q0fBJHADweO8rFmexO5Jnqagv/AcJZ6TG4U7Kszwzmhc2xT99e8HELze0CgFdxyiAhE6Vd57w+VYAwErlq4RBXzuoecF/5jA=',
      uuid_user: uuid,
      uuid_branch: branch[0]?.uuid_branch,
      uuid_sale_point: selectedSalePoint?.uuid,
      entry_date: selectedDate,
      coupons: tableData.map((coupon) => ({ uuid_coupon: coupon.uuid })),
    },
  };
};
